/**
 * Project Context Manager
 * 
 * Centralizes project context resolution and caching to ensure consistent
 * performance between private_label and white_label project types.
 * 
 * Now uses the global site config system as the single source of truth.
 * 
 * Addresses systemic issues:
 * - Multiple redundant domain lookups
 * - Race conditions in data loading
 * - Inconsistent project context persistence
 * - Performance degradation in private_label
 */

import { ref, computed, watch } from 'vue';
import { useProjectStore } from '@/store/project.store';
import { useRouterStore } from '@/store/router.store';
import { useUserStore } from '@/store/user.store';
import { getProjectType, getProjectId, getSiteConfig, setProjectId as setGlobalProjectId, isPrivateLabel, isWhiteLabel } from '@/config/global-site-config';

interface ProjectContext {
  projectId: string | null;
  projectType: 'white_label' | 'private_label' | null;
  projectInfo: any | null;
  isResolved: boolean;
  lastResolved: number;
  source: 'domain' | 'path' | 'cache' | 'store' | 'site_config';
  isDemoProject: boolean;
  isDemoDomain: boolean;
  isDemoMode: boolean;
}

interface SiteConfig {
  siteId: string;
  projectId: string | null;
  projectType: 'white_label' | 'private_label';
  description?: string;
  domains?: string[];
  buildTime?: string;
  buildTimestamp?: number;
  buildHash?: string;
}

interface ProjectContextCache {
  [key: string]: {
    projectContext: ProjectContext;
    timestamp: number;
    domain?: string;
    path?: string;
  };
}

class ProjectContextManager {
  private cache: ProjectContextCache = {};
  private currentContext = ref<ProjectContext>({
    projectId: null,
    projectType: null,
    projectInfo: null,
    isResolved: false,
    lastResolved: 0,
    source: 'cache',
    isDemoProject: false,
    isDemoDomain: false,
    isDemoMode: false
  });

  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly STORAGE_KEY = 'jello_project_context';
  private bootstrapCompleted = false;
  private decisionLog: Array<{ event: string; data: any; t: number }>= [];

  private recordDecision(event: string, data: any): void {
    try {
      this.decisionLog.push({ event, data, t: Date.now() });
      if (this.decisionLog.length > 100) {
        this.decisionLog.splice(0, this.decisionLog.length - 100);
      }
    } catch {}
  }

  constructor() {
    this.cleanupOldSiteConfigCache();
    // Skip persistent storage initialization - we want fresh site config every time
    // this.initializeFromPersistentStorage();
    // Skip store initialization during construction to avoid Pinia access issues
    // Stores will be initialized on first use when needed
    // Site config is now handled by the global system
  }

  /**
   * Aggressively clean up ALL old cache entries (no longer needed)
   */
  private cleanupOldSiteConfigCache(): void {
    if (typeof localStorage === 'undefined') return;
    
    try {
      // Remove old global cache key
      const oldGlobalKey = 'jello_site_config';
      if (localStorage.getItem(oldGlobalKey)) {
        localStorage.removeItem(oldGlobalKey);
        console.log('🧹 [ProjectContextManager] Removed old global site config cache');
      }
      
      // Remove old project context cache (contains wrong projectId from previous deployments)
      if (localStorage.getItem(this.STORAGE_KEY)) {
        localStorage.removeItem(this.STORAGE_KEY);
        console.log('🧹 [ProjectContextManager] Removed old project context cache');
      }
      
      // Remove any site-specific cache keys that might exist
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('jello_site_config_') || key?.startsWith('jello_')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log(`🧹 [ProjectContextManager] Removed old cache: ${key}`);
      });
      
      console.log('🧹 [ProjectContextManager] Aggressive cache cleanup completed - fresh start guaranteed');
      
    } catch (error) {
      console.warn('⚠️ [ProjectContextManager] Failed to cleanup old cache:', error);
    }
  }

  /**
   * Initialize stores and watchers when safe to do so
   */
  private ensureStoresInitialized(): void {
    // Only try to initialize stores if we haven't already
    if (!this.storesInitialized) {
      try {
        this.initializeFromStores();
        this.setupStoreWatchers();
        this.storesInitialized = true;
      } catch (error) {
        // If stores still aren't ready, we'll try again on next access
        console.warn('⚠️ [ProjectContextManager] Stores not ready, will try again later:', error);
      }
    }
  }

  private storesInitialized = false;

  /**
   * Detect demo mode based on project ID and domain
   */
  private detectDemoMode(projectId: string | null): { isDemoProject: boolean; isDemoDomain: boolean; isDemoMode: boolean } {
    // Check if project is demo
    const isDemoProject = projectId === 'demo' || projectId?.startsWith('demo_') || false;
    
    // Check if on demo domain
    let isDemoDomain = false;
    if (typeof window !== 'undefined') {
      isDemoDomain = window.location.hostname === 'demo.shir.ly' || 
                     window.location.hostname.includes('demo.shir.ly');
    }
    
    // Overall demo mode
    const isDemoMode = isDemoProject || isDemoDomain;
    
    console.log('🎭 [ProjectContextManager] Demo mode detection:', {
      projectId,
      isDemoProject,
      isDemoDomain,
      isDemoMode,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'server'
    });
    
    return { isDemoProject, isDemoDomain, isDemoMode };
  }

  // Site config is now handled by the global system - no local methods needed

  /**
   * Initialize from persistent storage (localStorage) if available
   */
  private initializeFromPersistentStorage(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;
      
      const parsed = JSON.parse(stored);
      const isExpired = Date.now() - parsed.timestamp > this.CACHE_TTL;
      
      if (!isExpired && parsed.projectContext?.projectId) {
        this.currentContext.value = {
          ...parsed.projectContext,
          source: 'cache'
        };
        console.log('🔄 [ProjectContextManager] Initialized from persistent storage:', {
          projectId: parsed.projectContext.projectId,
          projectType: parsed.projectContext.projectType,
          age: Date.now() - parsed.timestamp
        });
      }
    } catch (error) {
      console.warn('⚠️ [ProjectContextManager] Failed to load from persistent storage:', error);
    }
  }

  /**
   * Initialize from existing store state if available
   */
  private initializeFromStores(): void {
    const projectStore = useProjectStore();
    const routerStore = useRouterStore();

    // Only use store data if we don't already have context from persistent storage
    if (this.currentContext.value.projectId) {
      console.log('🔄 [ProjectContextManager] Already initialized from persistent storage, skipping store init');
      return;
    }

    if (projectStore.projectId && routerStore.projectType) {
      // Detect demo mode for the store-initialized context
      const demoModeDetection = this.detectDemoMode(projectStore.projectId);
      
      const newContext: ProjectContext = {
        projectId: projectStore.projectId,
        projectType: routerStore.projectType,
        projectInfo: projectStore.projectInfo,
        isResolved: true,
        lastResolved: Date.now(),
        source: 'store' as const,
        isDemoProject: demoModeDetection.isDemoProject,
        isDemoDomain: demoModeDetection.isDemoDomain,
        isDemoMode: demoModeDetection.isDemoMode
      };
      
      this.currentContext.value = newContext;
      this.persistContext(newContext);
      
      console.log('🔄 [ProjectContextManager] Initialized from existing stores:', {
        projectId: projectStore.projectId,
        projectType: routerStore.projectType
      });
    }
  }

  /**
   * Setup watchers to keep context in sync with stores
   */
  private setupStoreWatchers(): void {
    const projectStore = useProjectStore();
    const routerStore = useRouterStore();

    // Watch for project store changes
    watch(() => projectStore.projectId, (newProjectId) => {
      if (newProjectId && this.currentContext.value.projectId !== newProjectId) {
        this.currentContext.value.projectId = newProjectId;
        this.currentContext.value.lastResolved = Date.now();
        this.currentContext.value.isResolved = true;
        this.persistContext(this.currentContext.value);
        console.log('🔄 [ProjectContextManager] Updated projectId from store:', newProjectId);
        // Persist last project for white_label so logo navigation can use it next time
        try { localStorage.setItem('last_project_id', newProjectId); } catch {}
      }
    });

    // Watch for router store changes
    watch(() => routerStore.projectType, (newProjectType) => {
      if (newProjectType && this.currentContext.value.projectType !== newProjectType) {
        this.currentContext.value.projectType = newProjectType;
        this.currentContext.value.lastResolved = Date.now();
        this.persistContext(this.currentContext.value);
        console.log('🔄 [ProjectContextManager] Updated projectType from store:', newProjectType);
      }
    });
  }

  /**
   * Get cached context if valid
   */
  private getCachedContext(cacheKey: string): ProjectContext | null {
    const cached = this.cache[cacheKey];
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > this.CACHE_TTL;
    if (isExpired) {
      delete this.cache[cacheKey];
      return null;
    }

    console.log('✅ [ProjectContextManager] Using cached context:', cacheKey);
    
    // Ensure cached context has demo mode fields (backwards compatibility)
    const context = { ...cached.projectContext, source: 'cache' as const };
    if (context.isDemoMode === undefined) {
      const demoModeDetection = this.detectDemoMode(context.projectId);
      return {
        ...context,
        isDemoProject: demoModeDetection.isDemoProject,
        isDemoDomain: demoModeDetection.isDemoDomain,
        isDemoMode: demoModeDetection.isDemoMode
      };
    }
    
    return context;
  }

  /**
   * Cache project context
   */
  private setCachedContext(cacheKey: string, context: ProjectContext, metadata: { domain?: string; path?: string } = {}): void {
    this.cache[cacheKey] = {
      projectContext: { ...context },
      timestamp: Date.now(),
      ...metadata
    };
  }

  /**
   * Persist context to localStorage for synchronous access across page loads
   * DISABLED: We want fresh site config from static files every time
   */
  private persistContext(context: ProjectContext): void {
    // No longer persisting context - each site deployment has its own static config
    console.log('🚫 [ProjectContextManager] Context persistence disabled - using fresh site config:', context.projectId);
  }



  /**
   * Resolve project context synchronously using existing store data
   */
  resolveProjectContext(route: { path: string; params: any }, options: {
    skipDetection?: boolean;
    forceRefresh?: boolean;
  } = {}): ProjectContext {
    const { skipDetection = false, forceRefresh = false } = options;

    // Ensure stores are initialized when resolving context
    this.ensureStoresInitialized();

    // Return current context if already resolved and not forcing refresh
    if (!forceRefresh && this.currentContext.value.isResolved && this.currentContext.value.projectId) {
      console.log('🔄 [ProjectContextManager] Using existing resolved context:', this.currentContext.value.projectId);
      return this.currentContext.value;
    }

    console.log('🚀 [ProjectContextManager] Starting synchronous project context resolution:', {
      path: route.path,
      params: route.params,
      skipDetection,
      forceRefresh
    });
    this.recordDecision('start_resolve_context', { path: route.path, params: route.params, skipDetection, forceRefresh });

    // Step 1: Resolve project type synchronously
    const projectType = this.resolveProjectTypeSync();
    
    // Step 2: Resolve project ID synchronously based on type
    let projectId: string | null = null;
    let projectInfo: any = null;
    let source: ProjectContext['source'] = 'store';

    if (skipDetection) {
      // Use store values if available
      const projectStore = useProjectStore();
      projectId = projectStore.projectId;
      projectInfo = projectStore.projectInfo;
      source = 'store';
    } else {
      const resolution = this.resolveProjectIdSync(route, projectType);
      projectId = resolution.projectId;
      projectInfo = resolution.projectInfo;
      source = resolution.source;
    }

    // Step 3: Detect demo mode
    const demoModeDetection = this.detectDemoMode(projectId);

    // Step 4: Update context and stores synchronously
    const newContext: ProjectContext = {
      projectId,
      projectType,
      projectInfo,
      isResolved: true,
      lastResolved: Date.now(),
      source,
      isDemoProject: demoModeDetection.isDemoProject,
      isDemoDomain: demoModeDetection.isDemoDomain,
      isDemoMode: demoModeDetection.isDemoMode
    };

    this.currentContext.value = newContext;
    this.syncToStoresSync(newContext);
    this.persistContext(newContext);

    console.log('✅ [ProjectContextManager] Project context resolved synchronously:', {
      projectId,
      projectType,
      source,
      hasProjectInfo: !!projectInfo
    });
    this.recordDecision('end_resolve_context', { projectId, projectType, source, hasProjectInfo: !!projectInfo });

    return newContext;
  }

  /**
   * Resolve project type using the global site config system
   */
  private resolveProjectTypeSync(): 'white_label' | 'private_label' {
    const routerStore = useRouterStore();
    const projectType = getProjectType();
    
    console.log('⚡ [ProjectContextManager] Using global site config projectType:', projectType);
    
    // Sync to router store for components that still depend on it
    routerStore.setState({ key: 'projectType', value: projectType });
    
    return projectType;
  }

  /**
   * Resolve project ID synchronously using stores and cache
   */
  private resolveProjectIdSync(route: { path: string; params: any }, projectType: 'white_label' | 'private_label'): {
    projectId: string | null;
    projectInfo: any;
    source: ProjectContext['source'];
  } {
    const projectStore = useProjectStore();
    
    // FIRST: Check if we already have resolved context
    if (this.currentContext.value.isResolved && this.currentContext.value.projectId) {
      console.log('✅ [ProjectContextManager] Using existing resolved context (sync):', this.currentContext.value.projectId);
      return {
        projectId: this.currentContext.value.projectId,
        projectInfo: this.currentContext.value.projectInfo,
        source: this.currentContext.value.source
      };
    }

    // SECOND: Development Environment Domain Override
    const isDevelopment = import.meta.env.MODE === 'development';
    if (isDevelopment && import.meta.env.VITE_DOMAIN_OVERRIDE && projectType === 'private_label') {
      const domainOverride = import.meta.env.VITE_DOMAIN_OVERRIDE;
      console.log('🔧 [ProjectContextManager] Using environment domain override:', domainOverride);
      
      // If we have a site config that matches the domain override, use it
      const globalSiteConfig = getSiteConfig();
      if (globalSiteConfig && globalSiteConfig.domains?.some(d => domainOverride.includes(d))) {
        console.log('⚡ [ProjectContextManager] Site config matches domain override (0ms):', globalSiteConfig.projectId);
        return {
          projectId: globalSiteConfig.projectId,
          projectInfo: null,
          source: 'site_config'
        };
      }
      
      // Otherwise, this will fall through to domain mapping/Firestore lookup
    }

    // THIRD: Prioritize a path-based projectId for ALL project types when present
    // This ensures that navigating directly to "/:projectId/..." reliably switches context,
    // even for private_label deployments that normally rely on site config.
    const siteConfig = getSiteConfig();
    {
      const routeProjectId = (route.params?.projectId as string) || undefined;
      if (routeProjectId) {
        const currentGlobalId = getProjectId();
        console.groupCollapsed('🧭 [ProjectContextManager] Path projectId decision');
        console.log('routeProjectId:', routeProjectId, 'currentGlobalId:', currentGlobalId, 'projectType:', projectType, 'route.path:', route.path);
        if (currentGlobalId !== routeProjectId) {
          console.log('⚡ Overriding global projectId with path param');
          setGlobalProjectId(routeProjectId);
        } else {
          console.log('ℹ️ Global projectId already matches path param');
        }
        console.groupEnd();
        this.recordDecision('use_path_projectId', { routeProjectId, projectType, routePath: route.path });
        return {
          projectId: routeProjectId,
          // IMPORTANT: Do NOT carry over previous project's info when switching via path.
          // Using stale projectInfo for a different (possibly invalid) projectId causes
          // downstream components to assume valid data exists, leading to heavy retries
          // (e.g., Cloudinary 404 loops) and perceived hangs. Always reset to null here
          // and let guards/loaders populate fresh info once validation passes.
          projectInfo: null,
          source: 'path'
        };
      }
    }

    // Also support projectId from query string for white-label flows (e.g., /signup?projectId=...)
    if (projectType === 'white_label') {
      try {
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          const queryProjectId = params.get('projectId');
          if (queryProjectId) {
            const currentGlobalId = getProjectId();
            if (currentGlobalId !== queryProjectId) {
              console.log('⚡ [ProjectContextManager] White-label: using query projectId over site config:', { queryProjectId, siteProjectId: currentGlobalId });
              setGlobalProjectId(queryProjectId);
            }
            return {
              projectId: queryProjectId,
              projectInfo: null,
              source: 'path'
            };
          }
        }
      } catch (e) {
        // Ignore URL parsing errors and continue
      }
    }

    // Otherwise, check global site configuration (0ms lookup!)
    const globalProjectId = getProjectId();
    if (globalProjectId) {
      console.log('⚡ [ProjectContextManager] Using global site config (0ms):', {
        siteId: siteConfig?.siteId,
        projectId: globalProjectId,
        projectType: getProjectType()
      });
      this.recordDecision('use_site_config', { siteId: siteConfig?.siteId, projectId: globalProjectId, projectType: getProjectType() });
      return {
        projectId: globalProjectId,
        projectInfo: null, // Site config doesn't include full project info
        source: 'site_config'
      };
    }
    
    // For private_label or fallback, use store data (should be set during bootstrap)
    // Force a fresh read from the store to handle timing issues
    try {
      const currentProjectId = projectStore.$state.projectId || projectStore.projectId;
      const currentProjectInfo = projectStore.$state.projectInfo || projectStore.projectInfo;
      
      if (currentProjectId) {
        console.log('✅ [ProjectContextManager] Found projectId in store (sync):', currentProjectId);
        this.recordDecision('use_store_projectId', { projectId: currentProjectId });
        return {
          projectId: currentProjectId,
          projectInfo: currentProjectInfo,
          source: 'store'
        };
      }
    } catch (error) {
      console.warn('⚠️ [ProjectContextManager] Error accessing store state:', error);
    }
    
    // Check cache as last resort
    const cacheKey = `${projectType}_${route.path}_${JSON.stringify(route.params)}`;
    const cached = this.getCachedContext(cacheKey);
    if (cached && cached.projectId) {
      console.log('✅ [ProjectContextManager] Using cached projectId (sync):', cached.projectId);
      this.recordDecision('use_cached_projectId', { projectId: cached.projectId });
      return {
        projectId: cached.projectId,
        projectInfo: cached.projectInfo,
        source: 'cache'
      };
    }
    
    console.warn('🔄 [ProjectContextManager] No project ID found in stores or cache - may need async resolution');
    this.recordDecision('no_projectId_found', { routePath: route.path, params: route.params, projectType });
    return {
      projectId: null,
      projectInfo: null,
      source: 'store'
    };
  }

  /**
   * Sync context to stores synchronously with redundancy prevention
   */
  private syncToStoresSync(context: ProjectContext): void {
    const projectStore = useProjectStore();
    const routerStore = useRouterStore();

    // Track what actually gets updated to prevent redundant logs
    const updates: string[] = [];

    // Update router store only if value has changed
    if (context.projectType && routerStore.projectType !== context.projectType) {
      routerStore.setState({ key: 'projectType', value: context.projectType });
      updates.push(`projectType: ${context.projectType}`);
    }

    // Update project store only if values have changed
    if (context.projectId && projectStore.projectId !== context.projectId) {
      projectStore.setState({ key: 'projectId', value: context.projectId });
      updates.push(`projectId: ${context.projectId}`);
    }

    if (context.projectInfo && projectStore.projectInfo !== context.projectInfo) {
      projectStore.setState({ key: 'projectInfo', value: context.projectInfo });
      updates.push('projectInfo: updated');
    }

    // Log only if we actually made updates
    if (updates.length > 0) {
      console.log('🔄 [ProjectContextManager] Store sync updates:', updates.join(', '));
    }
  }

  /**
   * Ensure user projects are loaded synchronously (trigger loading if needed)
   */
  ensureUserProjectsLoaded(): void {
    const userStore = useUserStore();
    
    // CRITICAL: Check Firebase Auth directly and synchronously
    let firebaseAuthUser: any = null;
    try {
      // Try to access Firebase Auth synchronously - it should be available during bootstrap
      if (typeof window !== 'undefined' && (window as any).firebase?.auth?.()) {
        firebaseAuthUser = (window as any).firebase.auth().currentUser;
      }
    } catch (error) {
      // Ignore errors - fallback to store checks
    }
    
    // Check multiple authentication sources synchronously
    const hasStoreAuthUser = !!(userStore.authUser?.uid);
    const hasFirebaseAuthUser = !!(firebaseAuthUser?.uid);
    const isStoreAuthenticated = userStore.isAuthenticated;
    const isAuthenticated = isStoreAuthenticated || hasStoreAuthUser || hasFirebaseAuthUser;
    
    const authDebugInfo = {
      isStoreAuthenticated,
      hasStoreAuthUser,
      hasFirebaseAuthUser,
      storeAuthUid: userStore.authUser?.uid || 'none',
      firebaseAuthUid: firebaseAuthUser?.uid || 'none',
      finalIsAuthenticated: isAuthenticated
    };
    
    if (!isAuthenticated) {
      console.log('🔄 [ProjectContextManager] User not authenticated, skipping user projects load:', authDebugInfo);
      return;
    }

    if (userStore.$state.boundItems.has('user_projects')) {
      console.log('✅ [ProjectContextManager] User projects already loaded');
      return;
    }

    console.log('🔄 [ProjectContextManager] Triggering user projects load...', authDebugInfo);
    // Trigger loading asynchronously but don't await it - profile loader can continue
    userStore.fetchUserProjects().then(() => {
      console.log('✅ [ProjectContextManager] User projects loaded:', userStore.userProjects?.length || 0);
    }).catch((error) => {
      console.error('❌ [ProjectContextManager] Error loading user projects:', error);
    });
  }

  /**
   * Called from main.ts bootstrap after projectId is successfully detected
   */
  notifyBootstrapComplete(projectId: string, projectType: 'white_label' | 'private_label', projectInfo?: any): void {
    console.log('🚀 [ProjectContextManager] Bootstrap notification received:', { projectId, projectType });
    
    // Detect demo mode for bootstrap notification
    const demoModeDetection = this.detectDemoMode(projectId);
    
    const context: ProjectContext = {
      projectId,
      projectType,
      projectInfo: projectInfo || null,
      isResolved: true,
      lastResolved: Date.now(),
      source: 'domain',
      isDemoProject: demoModeDetection.isDemoProject,
      isDemoDomain: demoModeDetection.isDemoDomain,
      isDemoMode: demoModeDetection.isDemoMode
    };
    
    this.currentContext.value = context;
    this.persistContext(context);
    this.bootstrapCompleted = true;
    
    console.log('✅ [ProjectContextManager] Bootstrap context cached for synchronous access');
  }

  /**
   * Get current site configuration (delegates to global system)
   */
  getSiteConfig(): SiteConfig | null {
    return getSiteConfig();
  }

  /**
   * Manually set site configuration (useful for testing)
   * Note: This is deprecated - use the global system instead
   */
  setSiteConfig(config: SiteConfig | null): void {
    console.warn('⚠️ [ProjectContextManager] setSiteConfig is deprecated - use global config system instead');
    // For backwards compatibility, you could implement this if needed
    // But better to migrate callers to use the global system directly
  }

  /**
   * Clear cache (useful for testing or forced refresh)
   */
  clearCache(): void {
    this.cache = {};
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.STORAGE_KEY);
        // Note: Site config caching removed - each site has its own static config
      }
    } catch (error) {
      console.warn('⚠️ [ProjectContextManager] Failed to clear persistent storage:', error);
    }
    console.log('🧹 [ProjectContextManager] Cache cleared');
  }

  /**
   * Get current context (reactive)
   */
  get context() {
    return computed(() => this.currentContext.value);
  }

  /**
   * Check if context is resolved
   */
  get isContextResolved() {
    return computed(() => this.currentContext.value.isResolved);
  }

  /**
   * Get current demo mode state
   */
  getCurrentDemoMode(): { isDemoProject: boolean; isDemoDomain: boolean; isDemoMode: boolean } {
    const context = this.currentContext.value;
    return {
      isDemoProject: context.isDemoProject,
      isDemoDomain: context.isDemoDomain,
      isDemoMode: context.isDemoMode
    };
  }

  /**
   * Get reactive demo mode state
   */
  get demoMode() {
    return computed(() => ({
      isDemoProject: this.currentContext.value.isDemoProject,
      isDemoDomain: this.currentContext.value.isDemoDomain,
      isDemoMode: this.currentContext.value.isDemoMode
    }));
  }
}

// Export singleton instance
export const projectContextManager = new ProjectContextManager();

// Export composable for Vue components
export function useProjectContext() {
  return {
    context: projectContextManager.context,
    isResolved: projectContextManager.isContextResolved,
    demoMode: projectContextManager.demoMode,
    resolveContext: projectContextManager.resolveProjectContext.bind(projectContextManager),
    ensureUserProjectsLoaded: projectContextManager.ensureUserProjectsLoaded.bind(projectContextManager),
    clearCache: projectContextManager.clearCache.bind(projectContextManager),
    notifyBootstrapComplete: projectContextManager.notifyBootstrapComplete.bind(projectContextManager),
    getSiteConfig: projectContextManager.getSiteConfig.bind(projectContextManager),
    setSiteConfig: projectContextManager.setSiteConfig.bind(projectContextManager)
  };
}

// Debug helpers
if (typeof window !== 'undefined') {
  (window as any).projectContextDebug = {
    get() {
      try {
        return projectContextManager['currentContext']?.value || null;
      } catch { return null; }
    },
    history() {
      try {
        return projectContextManager['decisionLog']?.slice?.() || [];
      } catch { return []; }
    },
    forceResolve(path: string, params: any = {}) {
      try {
        return projectContextManager.resolveProjectContext({ path, params }, { forceRefresh: true });
      } catch (e) {
        console.warn('[projectContextDebug.forceResolve] failed', e);
        return null;
      }
    },
    clearCache() {
      try { projectContextManager.clearCache(); } catch {}
    }
  };
  console.log('🔧 projectContextDebug helpers attached to window');
}
